import { LevelGauge } from './level-gauge.js';
import type { InventoryStockLevelKey } from './stock-level-key.js';
import { INVENTORY_STOCK_LEVEL_LABELS } from './stock-level-labels.js';
import type { InventoryItemStockLevel, StockLevelWarehouse } from './types.js';
import { inventoryWarehouseStockLevel } from './warehouse-stock-level.js';

type StockLevelCellProps = Readonly<
  (
    | { level: InventoryItemStockLevel; warehouse?: never }
    | { warehouse: StockLevelWarehouse; level?: never }
  ) & { title?: string }
>;

const KEY_TEXT_CLASS: Record<InventoryStockLevelKey, string> = {
  OUT_OF_STOCK: 'text-destructive',
  BELOW_REORDER: 'text-amber-700 dark:text-amber-300',
  OVERSTOCKED: 'text-sky-700 dark:text-sky-300',
  IN_RANGE: 'text-foreground',
  NO_LEVELS: 'text-muted-foreground',
  NEVER_STOCKED: 'text-muted-foreground',
};

function warehouseLevel(warehouse: StockLevelWarehouse): InventoryItemStockLevel {
  const key = inventoryWarehouseStockLevel(warehouse);
  return {
    key,
    label: INVENTORY_STOCK_LEVEL_LABELS[key],
    second: '',
    gaugeWarehouse: key === 'NO_LEVELS' ? null : warehouse,
  };
}

const LABEL_SCOPE_SEPARATOR = ' · ';

function splitLabel(label: string): { status: string; scope: string } {
  const separatorIdx = label.indexOf(LABEL_SCOPE_SEPARATOR);
  if (separatorIdx === -1) return { status: label, scope: '' };
  return {
    status: label.slice(0, separatorIdx),
    scope: label.slice(separatorIdx + LABEL_SCOPE_SEPARATOR.length),
  };
}

export function StockLevelCell({ level, warehouse, title }: StockLevelCellProps) {
  const { key, label, second, gaugeWarehouse } = level ?? warehouseLevel(warehouse);
  const { status, scope } = splitLabel(label);
  return (
    <div className="flex min-w-0 max-w-52 flex-col gap-0.5" title={title ?? label}>
      <span
        data-testid="stock-level-label"
        className={`inline-flex items-center gap-1.5 font-medium ${KEY_TEXT_CLASS[key]}`}
      >
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
        <span>{status}</span>
      </span>
      {scope !== '' && (
        <span className="truncate text-xs text-muted-foreground">
          <span className="sr-only">{LABEL_SCOPE_SEPARATOR}</span>
          {scope}
        </span>
      )}
      {second !== '' && <span className="truncate text-xs text-muted-foreground">{second}</span>}
      {gaugeWarehouse && (
        <LevelGauge
          levelOnHand={gaugeWarehouse.levelOnHand}
          reorderPoint={gaugeWarehouse.reorderPoint}
          orderUpToLevel={gaugeWarehouse.orderUpToLevel}
        />
      )}
    </div>
  );
}
